import React, { useState, useEffect, useMemo } from 'react';
import { PageHeader } from './ui/PageHeader';
import { Button } from './ui/Button';
import { EmptyState } from './ui/EmptyState';

const ACTION_CONFIG = {
  create: {
    label: '新規登録',
    icon: 'fa-plus-circle',
    color: 'text-success',
    bg: 'bg-success-bg',
    border: 'border-success-border',
    badge: 'bg-success-bg text-success-foreground border border-success-border',
    dot: 'bg-success',
  },
  update: {
    label: '更新',
    icon: 'fa-edit',
    color: 'text-info',
    bg: 'bg-info-bg',
    border: 'border-info-border',
    badge: 'bg-info-bg text-info-foreground border border-info-border',
    dot: 'bg-info',
  },
  delete: {
    label: '削除',
    icon: 'fa-trash-alt',
    color: 'text-danger',
    bg: 'bg-danger-bg',
    border: 'border-danger-border',
    badge: 'bg-danger-bg text-danger-foreground border border-danger-border',
    dot: 'bg-danger',
  },
};

function formatDate(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isToday = d.toDateString() === today.toDateString();
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const timeStr = d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `今日 ${timeStr}`;
  if (isYesterday) return `昨日 ${timeStr}`;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' }) + ` ${timeStr}`;
}

function formatDateGroup(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return '今日';
  if (d.toDateString() === yesterday.toDateString()) return '昨日';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
}

function groupByDate(logs) {
  const groups = [];
  let currentDate = null;
  let currentItems = [];
  logs.forEach(log => {
    const dateKey = new Date(log.created_at).toDateString();
    if (dateKey !== currentDate) {
      if (currentItems.length > 0) {
        groups.push({ date: currentDate, label: formatDateGroup(currentItems[0].created_at), items: currentItems });
      }
      currentDate = dateKey;
      currentItems = [log];
    } else {
      currentItems.push(log);
    }
  });
  if (currentItems.length > 0) {
    groups.push({ date: currentDate, label: formatDateGroup(currentItems[0].created_at), items: currentItems });
  }
  return groups;
}

export default function ActivityTimeline() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all'); // 'all' | 'create' | 'update' | 'delete'
  const [filterUser, setFilterUser] = useState('all');

  // DB からログを読み込む（30秒ポーリング）
  const fetchLogs = () => {
    fetch('/api/activity-logs/')
      .then(res => res.json())
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => {
    fetchLogs();
    const timer = setInterval(fetchLogs, 10000);
    return () => clearInterval(timer);
  }, []);

  // ユーザー一覧
  const userList = useMemo(() => {
    const names = [...new Set(logs.map(l => l.user_name).filter(Boolean))];
    return names.sort();
  }, [logs]);

  // フィルタ・検索後のログ
  const filtered = useMemo(() => {
    return logs.filter(log => {
      if (filterAction !== 'all' && log.action !== filterAction) return false;
      if (filterUser !== 'all' && log.user_name !== filterUser) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        return (
          (log.target_name || '').toLowerCase().includes(q) ||
          (log.user_name || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [logs, filterAction, filterUser, search]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  // ログをクリア（確認あり）
  const handleClear = () => {
    if (!window.confirm('すべての操作履歴を削除しますか？この操作は元に戻せません。')) return;
    fetch('/api/activity-logs/clear/', { method: 'DELETE' })
      .then(() => setLogs([]))
      .catch(() => alert('削除に失敗しました'));
  };

  return (
    <div className="flex flex-col h-full bg-prodia-background text-prodia-text-primary">
      <PageHeader
        title="タイムライン"
        description="全操作履歴（最新 100 件）"
        actions={
          <>
            <span className="text-xs text-prodia-text-muted">計 {filtered.length} 件</span>
            {logs.length > 0 && (
              <Button variant="danger" size="sm" onClick={handleClear}>
                <i className="fas fa-trash text-[10px]"></i>
                履歴を全削除
              </Button>
            )}
          </>
        }
        filters={
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-prodia-text-muted text-xs"></i>
              <input
                type="text"
                placeholder="名前・対象で検索..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-8 py-2 text-sm bg-prodia-surface border border-prodia-border rounded-lg focus:outline-none focus:ring-2 focus:ring-prodia-primary/30 placeholder:text-prodia-text-muted"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-prodia-text-muted hover:text-prodia-text-secondary"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
            <div className="flex gap-1 bg-prodia-background border border-prodia-border rounded-lg p-1">
              {[
                { key: 'all', label: 'すべて', active: 'bg-prodia-surface text-prodia-text-primary shadow-prodia' },
                { key: 'create', label: '登録', active: 'bg-success-bg text-success-foreground shadow-prodia' },
                { key: 'update', label: '更新', active: 'bg-info-bg text-info-foreground shadow-prodia' },
                { key: 'delete', label: '削除', active: 'bg-danger-bg text-danger-foreground shadow-prodia' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFilterAction(opt.key)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    filterAction === opt.key ? opt.active : 'text-prodia-text-secondary hover:bg-prodia-surface'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {userList.length > 1 && (
              <select
                value={filterUser}
                onChange={e => setFilterUser(e.target.value)}
                className="px-3 py-2 text-sm bg-prodia-surface border border-prodia-border rounded-lg focus:outline-none focus:ring-2 focus:ring-prodia-primary/30 text-prodia-text-primary"
              >
                <option value="all">全ユーザー</option>
                {userList.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5">
        {logs.length === 0 ? (
          <EmptyState
            icon="fas fa-history"
            title="操作履歴がまだありません"
            description="エンジニアを登録・更新・削除すると履歴が記録されます"
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="fas fa-search"
            title="検索条件に一致する履歴がありません"
            actionLabel="フィルタをリセット"
            onAction={() => { setSearch(''); setFilterAction('all'); setFilterUser('all'); }}
          />
        ) : (
          <div className="max-w-2xl mx-auto space-y-8">
            {grouped.map(group => (
              <div key={group.date}>
                {/* 日付セパレータ */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-semibold text-prodia-text-secondary bg-prodia-background border border-prodia-border px-3 py-1 rounded-full">
                    {group.label}
                  </span>
                  <div className="flex-1 h-px bg-prodia-border"></div>
                  <span className="text-xs text-prodia-text-muted">{group.items.length} 件</span>
                </div>

                <div className="relative">
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-prodia-border rounded-full"></div>

                  <div className="space-y-5">
                    {group.items.map((log, idx) => {
                      const cfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.update;
                      return (
                        <div key={log.id ?? idx} className="flex gap-5">
                          {/* アイコンバッジ */}
                          <div className="relative z-10 flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm border-2 border-white ${cfg.bg}`}>
                              <i className={`fas ${cfg.icon} text-sm ${cfg.color}`}></i>
                            </div>
                          </div>

                          {/* カード */}
                          <div className={`flex-1 rounded-xl border px-5 py-4 bg-prodia-surface ${cfg.border} shadow-prodia`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.badge}`}>
                                <i className={`fas ${cfg.icon} text-[10px]`}></i>
                                {cfg.label}
                              </span>
                              <span className="text-xs text-prodia-text-muted flex items-center gap-1">
                                <i className="fas fa-clock text-[10px]"></i>
                                {formatDate(log.created_at)}
                              </span>
                            </div>
                            <p className="text-base font-semibold text-prodia-text-primary leading-snug">
                              {log.target_name}
                            </p>
                            <p className="text-sm text-prodia-text-secondary mt-1.5 flex items-center gap-1.5">
                              <i className="fas fa-user text-xs"></i>
                              <span className="font-medium text-prodia-text-primary">{log.user_name}</span>
                              <span>が{cfg.label}しました</span>
                            </p>
                            {log.details && (
                              <p className="text-xs text-prodia-text-muted mt-2.5 pl-3 border-l-2 border-prodia-border leading-relaxed">
                                {log.details}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
